import {useQueryClient} from '@tanstack/react-query';
import {FeaturesEnum} from 'presentation/constants';
import {PlanTypeEnum} from 'presentation/constants/bookingTypesEnum';
import {useHostName} from 'presentation/hooks';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {useExamOrientationsInfoStore} from 'presentation/modules/booking/exam/ui/pages/ExamBooking/steps/ExamSelection/components/ExamOrientationsDialog/store/useExamOrientationsInfoStore';
import {useHealthProcedureDataQuery} from 'presentation/modules/guide/health/queries/useHealthProcedureDataQuery';
import {useHealthSpecialtiesGuideQuery} from 'presentation/modules/guide/health/queries/useHealthSpecialtiesGuideQuery';
import {useOdontoSpecialtiesGuideQuery} from 'presentation/modules/guide/odonto/queries/useOdontoSpecialtiesGuideQuery';
import {useGuideNavigation} from 'presentation/modules/guide/shared/hooks/useGuideNavigation/useGuideNavigation';
import {useNetworkFiltersGuideQuery} from 'presentation/modules/guide/shared/queries/useNetworkFiltersGuideQuery/useNetworkFiltersGuideQuery';
import {useServicesGuideQuery} from 'presentation/modules/guide/shared/queries/useServicesGuideQuery';
import {ppoNetworkTypeEnum} from 'presentation/modules/guide/shared/store/guideStore/types';
import {usePpoNetworkStore} from 'presentation/modules/guide/shared/store/guideStore/usePpoNetworkStore';
import {examsPreparationQueryOptions} from 'presentation/modules/myAppointments/ui/components/PrepareModalContent/queries/useGetExamsPreparationQuery';
import {useAccountInformation, useMediaQueries, useQueryParams} from 'presentation/pages/private';
import {useGetHealthCardQuery} from 'presentation/pages/private/health-card/queries/useGetHealthCardQuery';
import {useCallback, useState} from 'react';

export function useSearchDetails(type: PlanTypeEnum) {
	const {isEnabled: isSemanticSearchEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_SEMANTIC_SEARCH_MEDICAL_GUIDE,
	);
	const {isGndiSaoPaulo, isNotredameSaude} = useHostName();
	const {
		isPPOPlan,
		beneficiary: {idadeC},
	} = useAccountInformation();
	const {isMobile} = useMediaQueries();
	const {data: healthCard, isLoading: healthCardIsLoading} = useGetHealthCardQuery();
	const {SEARCH_PARAMS} = useQueryParams();
	const ppoNetworkTypeState = usePpoNetworkStore(state => state.ppoNetworkType);
	const {isEnabled: isOnDemandExamsEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_ON_DEMAND_EXAMS,
	);
	const queryClient = useQueryClient();
	const [openOnDemandExamDrawer, setOpenOnDemandExamDrawer] = useState(false);
	const [isOnDemandExamLoading, setIsOnDemandExamLoading] = useState(false);
	const setExamOrientationsInfo = useExamOrientationsInfoStore(
		state => state.setExamOrientationInfo,
	);
	const resetExamOrientationInfo = useExamOrientationsInfoStore(
		state => state.resetExamOrientationInfo,
	);
	const hasExamPreparation = useExamOrientationsInfoStore(
		state => !!state.examOrientationInfo.orientations,
	);
	const [examsOrientationsModal, setExamOrientationsModal] = useState(false);

	const {ufCode, cityName, serviceType, specialtyCode, networkCode, cd_procedimento, specialtyName} =
		SEARCH_PARAMS;

	const {refetch: refetchProcedureData} = useHealthProcedureDataQuery({
		cdProcedimento: Number(cd_procedimento),
		enabled: false,
	});

	const handleCloseOnDemandExamDrawer = () => {
		setOpenOnDemandExamDrawer(false);
		resetExamOrientationInfo();
	};

	const handleViewOnDemandExamUnits = () => {
		setOpenOnDemandExamDrawer(false);
		setExamOrientationsModal(true);
	};

	const handleChangeExamOrientationsModal = (open: boolean) => {
		setExamOrientationsModal(open);
		if (!open) resetExamOrientationInfo();
	};

	const handleOpenOnDemandExamDrawer = () => {
		setOpenOnDemandExamDrawer(true);
	};

	// 204 No Content returns an empty body, so there is no dsDescricao
	const fetchExamPreparation = async (cdTipoExame: string) => {
		try {
			const examPreparation = await queryClient.fetchQuery(
				examsPreparationQueryOptions({cdTipoExame, nuIdade: String(idadeC)}),
			);
			return examPreparation?.dsDescricao ?? '';
		} catch {
			return '';
		}
	};

	const handleSearchResults = async (onSearchResults: () => void) => {
		if (!isOnDemandExamsEnabled || type !== PlanTypeEnum.HEALTH || !cd_procedimento) {
			onSearchResults();
			return;
		}

		setIsOnDemandExamLoading(true);
		try {
			const {data: procedureData} = await refetchProcedureData();

			if (!procedureData?.flAgendamentoLivre) {
				onSearchResults();
				return;
			}

			const cdTipoExame = String(procedureData.cdTipoExame);
			const orientations = await fetchExamPreparation(cdTipoExame);

			setExamOrientationsInfo({
				code: cdTipoExame,
				examName: specialtyName,
				orientations,
				procedureCode: cd_procedimento,
			});
			setOpenOnDemandExamDrawer(true);
		} finally {
			setIsOnDemandExamLoading(false);
		}
	};

	const shouldShowNetworkFilter =
		type === PlanTypeEnum.HEALTH &&
		((isGndiSaoPaulo && isPPOPlan) ||
			(isNotredameSaude && ppoNetworkTypeState === ppoNetworkTypeEnum.ALL));

	const cdPlanoANS =
		type === PlanTypeEnum.HEALTH
			? healthCard?.registroPlanoAns || ''
			: healthCard?.registroPlanoAnsOdonto?.trim() || healthCard?.registroPlanoAns || '';

	const {handleNavToStateSelection} = useGuideNavigation({type});

	const {
		data: services,
		isLoading: servicesLoading,
		isError: servicesError,
		refetch: refetchServices,
	} = useServicesGuideQuery({
		type,
		cdUf: ufCode,
		nmCidade: cityName,
	});

	const {
		data: networkFilters,
		isLoading: networkLoading,
		isError: networkError,
		refetch: refetchNetworkFilters,
	} = useNetworkFiltersGuideQuery({
		enabled: shouldShowNetworkFilter,
	});

	const {
		data: healthSpecialties,
		isLoading: healthSpecialtiesLoading,
		isError: healthSpecialtiesError,
		refetch: refetchHealthSpecialties,
	} = useHealthSpecialtiesGuideQuery({
		cdPlanoANS,
		cdUf: ufCode,
		nmCidade: cityName,
		nmServico: serviceType,
		enabled: type === PlanTypeEnum.HEALTH,
	});

	const {
		data: odontoSpecialties,
		isLoading: odontoSpecialtiesLoading,
		isError: odontoSpecialtiesError,
		refetch: refetchOdontoSpecialties,
	} = useOdontoSpecialtiesGuideQuery({
		cdPlanoANS,
		cdUf: ufCode,
		service: serviceType,
		enabled: type === PlanTypeEnum.ODONTO,
	});

	const hasErrors =
		servicesError ||
		(shouldShowNetworkFilter && networkError) ||
		(type === PlanTypeEnum.HEALTH && healthSpecialtiesError) ||
		(type === PlanTypeEnum.ODONTO && odontoSpecialtiesError);

	const handleRetryFailedQueries = useCallback(async () => {
		const retryPromises: Promise<unknown>[] = [];

		if (servicesError) {
			retryPromises.push(refetchServices());
		}

		if (shouldShowNetworkFilter && networkError) {
			retryPromises.push(refetchNetworkFilters());
		}

		if (type === PlanTypeEnum.HEALTH && healthSpecialtiesError) {
			retryPromises.push(refetchHealthSpecialties());
		}

		if (type === PlanTypeEnum.ODONTO) {
			if (odontoSpecialtiesError) {
				retryPromises.push(refetchOdontoSpecialties());
			}
		}

		await Promise.allSettled(retryPromises);
	}, [
		servicesError,
		networkError,
		healthSpecialtiesError,
		odontoSpecialtiesError,
		shouldShowNetworkFilter,
		type,
		refetchServices,
		refetchNetworkFilters,
		refetchHealthSpecialties,
		refetchOdontoSpecialties,
	]);

	return {
		isSemanticSearchEnabled,
		isMobile,
		shouldShowNetworkFilter,
		services,
		servicesLoading,
		networkFilters,
		networkLoading,
		healthSpecialties,
		healthSpecialtiesLoading,
		odontoSpecialties,
		odontoSpecialtiesLoading,
		hasErrors,
		handleRetryFailedQueries,
		handleNavToStateSelection,
		healthCardIsLoading,
		specialtyCode,
		networkCode,
		serviceType,
		cd_procedimento,
		openOnDemandExamDrawer,
		handleCloseOnDemandExamDrawer,
		handleOpenOnDemandExamDrawer,
		handleSearchResults,
		isOnDemandExamLoading,
		specialtyName,
		hasExamPreparation,
		examsOrientationsModal,
		setExamOrientationsModal: handleChangeExamOrientationsModal,
		handleViewOnDemandExamUnits,
	};
}
